<template>
  <b-navbar-nav
    class="profile align-items-stretch justify-content-center flex-column bg-md-dark cursor-pointer"
    :class="{'expanded': showCollapse}"
  >
    <div class="position-relative w-100 h-100 d-flex align-items-center justify-content-between px-2" @click="showCollapse = !showCollapse">
      <div class="d-flex">
        <b-avatar
          :variant="showCollapse ? 'light' : 'dark'"
          size="3em"
          class="bg-transparent me-md-2"
        >
          <b-icon icon="bobcaticon-user" class="profile-avatar-icon" fixed-width />
        </b-avatar>
        <div
          class="text-white d-none d-md-flex flex-column"
          :class="{'justify-content-center': !regionActive}"
        >
          <div class="text-nowrap">{{ user.name }}</div>
          <div v-if="regionActive" class="text-nowrap small">{{ regionActive }}</div>
        </div>
      </div>
      <a class="text-white d-none d-md-block dropdown-toggle ps-2 pe-3"
         href="#"
         role="button"
         data-toggle="dropdown"
         aria-haspopup="true"
         :aria-expanded="showCollapse ? 'true' : 'false'"
      />
    </div>

    <transition name="fade">
      <div v-show="showCollapse" id="profile-flyout" class="bg-dark py-3">
        <ul class="list-unstyled mb-0">
          <li class="d-md-none fw-normal text-white text-center mb-3 list-unstyled">
            <p class="mb-0">{{ user.name }}</p>
            <p class="small mb-0">{{ currentRegionLabel }} {{ regionActive }}</p>
          </li>
          <slot @click="hide">
            <b-nav-item
              v-for="region in regionsList"
              :key="region.key"
              :disabled="region.disabled"
              link-class="px-3 py-2"
              class="bg-xs-black position-relative font-condensed"
              @click="changeRegion(region.key)"
            >
              <b-icon icon="bobcaticon-arrowrightleft" class="profile-icon" fixed-width />
              <span class="ms-2 text-white profile-label">{{ region.translation }}</span>
            </b-nav-item>
            <b-nav-item
              link-class="px-3 py-2"
              class="bg-xs-black position-relative font-condensed"
              @click="logout"
            >
              <b-icon icon="bobcaticon-logout" class="profile-icon profile-icon-large" fixed-width />
              <span class="ms-2 text-white profile-label">{{ logoutLabel }}</span>
            </b-nav-item>
          </slot>
        </ul>
      </div>
    </transition>

  </b-navbar-nav>

  <Teleport to="body">
    <transition name="fade">
      <div v-if="showCollapse"
           class="profile-menu-backdrop"
           @click="showCollapse = false"
      />
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useSharedCollapse } from '../composables/useSharedCollapse'

interface Region {
  key: string
  translation: string
  active: boolean
  disabled?: boolean
}

interface User {
  name: string
  [key: string]: unknown
}

interface Props {
  user?: User
  logoutLabel: string
  regions: Region[]
  currentRegionLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  regions: () => []
})

const emit = defineEmits<{
  logout: []
  changeRegion: [region: string]
}>()

const { showCollapse } = useSharedCollapse('profile-flyout')

const regionsList = computed<Region[]>(() =>
  props.regions.filter((region: Region) => region.active === false)
)

const regionActive = computed<string>(() => {
  const active = props.regions.find((region: Region) => region.active === true)
  return active ? active.translation : ''
})

const avatarBadge = computed<string | false>(() => {
  if (props.regions.length === 0) return false
  return props.regions.length.toString()
})

function hide(): void {
  showCollapse.value = false
}

function logout(): void {
  emit('logout')
}

function changeRegion(region: string): void {
  emit('changeRegion', region)
  showCollapse.value = false
}

defineExpose({ hide })
</script>

<style lang="scss">

.profile-menu-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1034; // just below #header (1035), above page content
  cursor: pointer;
}
</style>

<style lang="scss" scoped>
@import '../scss/grid';
@import '../scss/colors';
@import '../scss/transitions';

$profile-width: 22.375rem;

.profile {
  align-self: stretch; // fill the full 4rem header height
  height: 4rem; // explicit height so h-100 on inner div resolves correctly

  @include media-breakpoint-up('md') {
    width: $profile-width;
  }

  &.expanded {
    background: $white;
    @include media-breakpoint-up('md') {
      background: $gray-800;
    }
  }
  .dropdown-toggle {
    line-height: 0;
    &::after {
      border-top: 0.55em solid;
      border-right: 0.45em solid transparent;
      border-bottom: 0;
      border-left: 0.45em solid transparent;
      transition: $transition-base;
    }

    &[aria-expanded="true"] {
      &::after {
        transform: rotate(180deg);
      }
    }
  }

  &-icon {
    color: $white;
    font-size: 7px;
    width: 24px;
    height: 24px;

    &-large {
      font-size: 16px;
    }

    :deep(bicon) {
      width: inherit;
    }

    .bobcaticon-arrowrightleft::before{
      font-size: 12px;
    }

    .bobcaticon-logout::before{
      font-size: 8px;
    }
  }

  &-label {
    vertical-align: middle;
  }

  &-avatar-icon {
      font-size: 24px;
      @include media-breakpoint-up('md') {
        color: $white;
      }
  }

  :deep(.b-avatar) {
    border: none !important;
    border-radius: 0 !important;
    margin-top: 2px;
  }
}

@include media-breakpoint-up('md') {
  .bg-md-dark {
    background-color: $dark;
  }
}

#profile-flyout {
  position: fixed;
  right: 0;
  left: 0;
  top: 4rem;
  width: 100%;
  max-height: calc(100vh - 4rem);
  overflow-y: auto;

  @include media-breakpoint-up('md') {
    left: auto;
    width: $profile-width;
  }

  .nav-item {
    &:hover {
      &:after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 0.25rem;
        background-color: $primary;
        z-index: -1;
      }
    }
  }
}
</style>
